import type { ReactNode } from 'react';
import { Box, Button, Center, Group, Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { PmTask } from '@/api/generated/models';
import {
  getGetPmDashboardSummaryQueryKey,
  useDismissPmTask
} from '@/api/generated/pm-dashboard/pm-dashboard';
import {
  CardSection,
  ListPagination,
  MutationButton,
  StatusPill,
  TABLE_DATE_COL_WIDTH,
  TABLE_STATUS_COL_WIDTH,
  tableCenteredCellStyle,
  tableRowStyle
} from '@/components/common';
import { PanelInfoIcon } from '@/features/board/components/PanelInfoIcon';
import {
  formatPmTaskSubtitle,
  formatPmTaskTitle,
  pmTaskActionHref,
  pmTaskActionLabelKey,
  pmTaskCategoryConfig,
  pmTaskPriorityColor,
  pmTaskPriorityLabelKey
} from '@/features/dashboard/pmDashboardDisplay';
import { formatDate } from '@/utils/format';
import { useLocalPagination } from '@/utils/useLocalPagination';

/** Ispod ovog broja stavki paging kontrole se uopće ne prikazuju (Figma NP-01M "Prikazano 6 od 6
 * stavki" — samo napomena, bez pagera) — sve stane na default stranicu (useLocalPagination = 10). */
const PAGING_THRESHOLD = 11;

const COLUMNS: [string, number][] = [
  ['pmDashboard.table.colItem', 280],
  ['pmDashboard.table.colContract', 200]
];

const MOBILE_HEADER_HEIGHT = 56;

const colFlex = (width: number) => ({ flex: `${width} 1 0px` });

export const PmTaskTable = ({
  title,
  info,
  tasks,
  mobileFilters,
  mobileFilterSummary,
  desktopFilters
}: {
  title: string;
  info?: ReactNode;
  tasks: PmTask[];
  mobileFilters?: ReactNode;
  mobileFilterSummary?: string;
  desktopFilters?: ReactNode;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { page, setPage, pageSize, setPageSize, totalPages, pageItems } = useLocalPagination(tasks);
  const stickySentinelRef = useRef<HTMLDivElement>(null);
  const [isHeaderStuck, setIsHeaderStuck] = useState(false);

  useEffect(() => {
    const sentinel = stickySentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) =>
        setIsHeaderStuck(
          !entry.isIntersecting && entry.boundingClientRect.top < MOBILE_HEADER_HEIGHT
        ),
      { rootMargin: `-${MOBILE_HEADER_HEIGHT}px 0px 0px 0px` }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);
  const dismissMutation = useDismissPmTask({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetPmDashboardSummaryQueryKey() });
      }
    }
  });

  // Isti cilj kao akcijski gumb na mobilnoj kartici — izvještaj/faktura/ugovor/ponuda prema tipu.
  const goToTask = (task: PmTask) => {
    const href = pmTaskActionHref(task);
    if (href) navigate(href);
  };

  const dismiss = (task: PmTask) =>
    task.taskKey && dismissMutation.mutate({ taskKey: task.taskKey });

  const showingLabel = t('pmDashboard.table.showing', {
    shown: pageItems.length,
    total: tasks.length
  });

  const mobileCards = pageItems.map((task) => {
    const categoryCfg = pmTaskCategoryConfig[task.category ?? 'TODO'];
    const actionHref = pmTaskActionHref(task);
    const dueDate = task.dueDate ?? task.referenceDate ?? task.completedAt;
    return (
      <Stack
        key={task.taskKey}
        gap={10}
        p={16}
        style={{
          borderRadius: 12,
          border: '1px solid var(--color-border-soft)',
          backgroundColor: 'var(--color-bg-surface)'
        }}
      >
        <Group justify="space-between" wrap="nowrap" align="center">
          <Group gap={6} wrap="nowrap">
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                flexShrink: 0,
                backgroundColor: pmTaskPriorityColor(task.priority)
              }}
            />
            <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
              {t(pmTaskPriorityLabelKey(task.priority))}
            </Text>
          </Group>
          <StatusPill label={t(categoryCfg.labelKey)} bg={categoryCfg.bg} fg={categoryCfg.fg} />
        </Group>
        <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
          {formatPmTaskTitle(t, task)}
        </Text>
        <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
          {formatPmTaskSubtitle(t, task)}
        </Text>
        <Group gap={8} wrap="nowrap">
          <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
            {t('pmDashboard.table.dueLabel', { date: formatDate(dueDate) })}
          </Text>
          {(task.overdueDays ?? 0) > 0 && (
            <Text fz={11} fw={600} style={{ color: 'var(--color-status-danger)' }}>
              {t('pmDashboard.table.overdueBadge', { days: task.overdueDays })}
            </Text>
          )}
        </Group>
        <div style={{ height: 1, width: '100%', backgroundColor: 'var(--color-border-soft)' }} />
        <Group gap={8}>
          {actionHref && (
            <Button size="xs" variant="default" onClick={() => navigate(actionHref)}>
              {t(pmTaskActionLabelKey(task))}
            </Button>
          )}
          {task.dismissible && (
            <MutationButton
              size="xs"
              variant="light"
              isPending={dismissMutation.isPending}
              onClick={() => dismiss(task)}
            >
              {t('pmDashboard.table.dismissAction')}
            </MutationButton>
          )}
        </Group>
      </Stack>
    );
  });

  // Stupac s gumbom "Odbaci" samo kad ga barem jedan zadatak na stranici ima.
  const hasDismissColumn = pageItems.some((task) => task.dismissible);

  const desktopTable = (
    <Box style={{ overflowX: 'auto' }}>
      <Box style={{ minWidth: 760 }}>
        <div
          className="table-header-row"
          style={{
            ...tableRowStyle,
            padding: '10px 20px',
            backgroundColor: 'var(--color-bg-canvas)',
            borderBottom: '1px solid var(--color-border-soft)'
          }}
        >
          {COLUMNS.map(([key, width]) => (
            <Text
              key={key}
              fz={10}
              fw={600}
              style={{
                color: 'var(--color-text-muted)',
                letterSpacing: 0.3,
                whiteSpace: 'pre-line',
                ...colFlex(width)
              }}
            >
              {t(key)}
            </Text>
          ))}
          <Text
            fz={10}
            fw={600}
            style={{
              width: TABLE_DATE_COL_WIDTH,
              flexShrink: 0,
              color: 'var(--color-text-muted)',
              letterSpacing: 0.3
            }}
          >
            {t('pmDashboard.table.colDueDate')}
          </Text>
          <Text
            fz={10}
            fw={600}
            ta="center"
            style={{
              width: TABLE_STATUS_COL_WIDTH,
              flexShrink: 0,
              color: 'var(--color-text-muted)',
              letterSpacing: 0.3
            }}
          >
            {t('pmDashboard.table.colCategory')}
          </Text>
          {hasDismissColumn && <div style={{ width: 110, flexShrink: 0 }} />}
        </div>

        {pageItems.map((task, index) => {
          const categoryCfg = pmTaskCategoryConfig[task.category ?? 'TODO'];
          return (
            <div
              key={task.taskKey}
              style={{
                ...tableRowStyle,
                padding: '14px 20px',
                borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
              }}
            >
              <Group gap={10} wrap="nowrap" style={colFlex(280)}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    flexShrink: 0,
                    backgroundColor: pmTaskPriorityColor(task.priority)
                  }}
                />
                <Text
                  fz={13.5}
                  fw={600}
                  role={task.contractId || task.offerId ? 'button' : undefined}
                  tabIndex={task.contractId || task.offerId ? 0 : undefined}
                  onClick={() => goToTask(task)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') goToTask(task);
                  }}
                  style={{
                    color: 'var(--color-text-primary)',
                    cursor: task.contractId || task.offerId ? 'pointer' : undefined
                  }}
                >
                  {formatPmTaskTitle(t, task)}
                </Text>
              </Group>
              <Stack gap={1} style={colFlex(200)}>
                <Text fz={13} fw={600} truncate style={{ color: 'var(--color-text-primary)' }}>
                  {task.contractNumber
                    ? t('pmDashboard.table.contractLabel', { number: task.contractNumber })
                    : t('pmDashboard.table.offerLabel', { number: task.offerNumber })}
                </Text>
                <Text fz={11.5} truncate style={{ color: 'var(--color-text-muted)' }}>
                  {[task.objectName, task.clientOrgName].filter(Boolean).join(' · ') || '—'}
                </Text>
              </Stack>
              <Stack gap={1} style={{ width: TABLE_DATE_COL_WIDTH, flexShrink: 0 }}>
                <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                  {formatDate(task.dueDate ?? task.referenceDate ?? task.completedAt)}
                </Text>
                {(task.overdueDays ?? 0) > 0 && (
                  <Text fz={11.5} fw={600} style={{ color: 'var(--color-status-danger)' }}>
                    {t('pmDashboard.table.overdueBadge', { days: task.overdueDays })}
                  </Text>
                )}
              </Stack>
              <div
                style={{ ...tableCenteredCellStyle, width: TABLE_STATUS_COL_WIDTH, flexShrink: 0 }}
              >
                <StatusPill
                  label={t(categoryCfg.labelKey)}
                  bg={categoryCfg.bg}
                  fg={categoryCfg.fg}
                />
              </div>
              {hasDismissColumn && (
                <div style={{ width: 110, flexShrink: 0 }}>
                  {task.dismissible && (
                    <MutationButton
                      size="xs"
                      variant="outline"
                      isPending={dismissMutation.isPending}
                      onClick={() => dismiss(task)}
                    >
                      {t('pmDashboard.table.dismissAction')}
                    </MutationButton>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <>
      <Stack gap={12} hiddenFrom="sm" w="100%">
        <div ref={stickySentinelRef} style={{ height: 0, marginBottom: -12 }} />
        <Group
          justify="space-between"
          align="center"
          wrap="nowrap"
          mx={-16}
          my={-8}
          px={16}
          py={8}
          style={{
            position: 'sticky',
            top: 'var(--app-shell-header-offset, 0px)',
            zIndex: 10,
            backgroundColor: 'var(--color-bg-canvas)',
            borderBottom: `1px solid ${isHeaderStuck ? 'var(--color-border-soft)' : 'transparent'}`,
            boxShadow: isHeaderStuck ? '0 2px 6px rgba(15, 23, 42, 0.06)' : 'none',
            transition: 'border-color 150ms ease, box-shadow 150ms ease'
          }}
        >
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Group gap={8} wrap="nowrap">
              <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                {title}
              </Text>
              {info && <PanelInfoIcon label={info} width={300} title={title} />}
            </Group>
            {mobileFilterSummary && (
              <Text fz={12} style={{ color: 'var(--color-text-secondary)' }}>
                {mobileFilterSummary}
              </Text>
            )}
          </Stack>
          {mobileFilters}
        </Group>
        {tasks.length === 0 ? (
          <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
            {t('pmDashboard.table.empty')}
          </Text>
        ) : (
          mobileCards
        )}
        {tasks.length > 0 &&
          (tasks.length < PAGING_THRESHOLD ? (
            <Text fz={11.5} style={{ color: 'var(--color-text-muted)' }}>
              {showingLabel}
            </Text>
          ) : (
            <ListPagination
              label={showingLabel}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              bordered={false}
            />
          ))}
      </Stack>

      {/* Desktop — nepromijenjeno, tablica unutar CardSection panela kao prije. */}
      <Box visibleFrom="sm">
        <CardSection
          title={title}
          headerRight={
            desktopFilters || info ? (
              <Group gap={12} wrap="nowrap">
                {desktopFilters}
                {info && <PanelInfoIcon label={info} width={300} title={title} />}
              </Group>
            ) : undefined
          }
        >
          {tasks.length === 0 ? (
            <Center py={32}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('pmDashboard.table.empty')}
              </Text>
            </Center>
          ) : (
            desktopTable
          )}
          {tasks.length > 0 &&
            (tasks.length < PAGING_THRESHOLD ? (
              <Text
                fz={11.5}
                style={{
                  color: 'var(--color-text-muted)',
                  padding: '14px 20px',
                  borderTop: '1px solid var(--color-border-soft)'
                }}
              >
                {showingLabel}
              </Text>
            ) : (
              <ListPagination
                label={showingLabel}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />
            ))}
        </CardSection>
      </Box>
    </>
  );
};
